import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { readFamilyBoard } from '../../db/familyDisplay.js';
import type { FamilyBoard } from '../../db/familyDisplay.js';
import { currentHouseholdId } from '../../db/shopping.js';
import { useToday } from '../../hooks/useToday.js';
import { TodayShopping } from '../TodayShopping.js';

/**
 * „Heute“ im Raum Haushalt (27.10.2026): was heute im Haushalt dran ist —
 * Einkaufstag und Liste, Haushaltstermine, geteilte Aufgaben, Sterne.
 *
 * Keine neue Fachlogik: Die Auswahl ist dieselbe wie an der Familienanzeige
 * (`readFamilyBoard` — nur Geteiltes des Haushalts, nie Privates, nie Geld),
 * hier in der Größe der App. Wie „Heute“ zeigt die Seite nur, was jetzt dran
 * ist; Einrichten und Ausführliches liegen in den Reitern daneben.
 */
export function HouseholdToday({
  planning,
  child,
  onOpenList,
  onOpenPlan,
  onOpenFamilyDisplay,
  onOpenMembers,
}: {
  /** Modul Planen (E15): ohne es keine Termine und Aufgaben. */
  planning: boolean;
  /** Kinder (E16) sehen keine Sterne der Geschwister und keine Familienanzeige. */
  child: boolean;
  onOpenList: (shopId: string | null) => void;
  onOpenPlan: () => void;
  onOpenFamilyDisplay?: (() => void) | undefined;
  /**
   * Mitglieder (27.10.2026) stehen nicht mehr in der Leiste — Einrichtung
   * gehört hinter einen Knopf, die Leiste bekam dafür „Essen“.
   */
  onOpenMembers?: (() => void) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const today = useToday();
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], undefined);
  const board = useLiveQuery(
    () =>
      householdId && today
        ? readFamilyBoard(householdId, today, db)
        : Promise.resolve(null as FamilyBoard | null),
    [householdId, today],
    undefined,
  );

  return (
    <section aria-labelledby="household-today" className="flex flex-col gap-3">
      <h2 id="household-today" className="sr-only">
        {t('householdToday.heading')}
      </h2>

      {householdId === null ? (
        <p role="status" className="kw-tile text-sm text-warn">
          {t('householdToday.noHousehold')}
        </p>
      ) : null}

      {onOpenMembers ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onOpenMembers}
            aria-describedby="household-members-hint"
            className="kw-btn kw-btn-quiet kw-btn-small px-4"
          >
            {t('nav.members')}
          </button>
          <span id="household-members-hint" className="sr-only">
            {t('householdToday.membersHint')}
          </span>
        </div>
      ) : null}

      <TodayShopping onOpenList={onOpenList} />

      {board && planning ? (
        <section aria-labelledby="household-today-events" className="kw-tile flex flex-col gap-2">
          <h3 id="household-today-events" className="kw-kicker">
            {t('householdToday.events')}
          </h3>
          {board.today.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('householdToday.noEvents')}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {board.today.map((event) => (
                <li key={event.id} className="flex gap-3 text-sm">
                  <span className="kw-figure w-16 shrink-0">
                    {event.time ?? t('householdToday.allDay')}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block font-medium break-words ${event.cancelled ? 'text-ink-muted line-through' : ''}`}
                    >
                      {event.title}
                    </span>
                    {event.location || event.forNames.length > 0 ? (
                      <span className="block text-xs text-ink-muted">
                        {[
                          event.location,
                          event.forNames.length > 0
                            ? t('householdToday.forWhom', { names: event.forNames.join(', ') })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {board && planning ? (
        <section aria-labelledby="household-today-tasks" className="kw-tile flex flex-col gap-2">
          <h3 id="household-today-tasks" className="kw-kicker">
            {t('householdToday.tasks')}
          </h3>
          {board.people.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('householdToday.noTasks')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {board.people.map((person) => (
                <li key={person.userId ?? 'all'} className="text-sm">
                  <span className="block font-medium">
                    {person.name ?? t('householdToday.everyone')} ·{' '}
                    {t('householdToday.taskCount', { count: person.tasks.length })}
                  </span>
                  <span className="block text-xs text-ink-muted">
                    {person.tasks.map((task) => task.title).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <button type="button" onClick={onOpenPlan} className="kw-btn kw-btn-quiet kw-btn-small">
            {t('householdToday.toPlan')}
          </button>
        </section>
      ) : null}

      {board ? (
        <section aria-labelledby="household-today-shopping" className="kw-tile flex flex-col gap-2">
          <h3 id="household-today-shopping" className="kw-kicker">
            {t('householdToday.shopping')}
          </h3>
          <p className="text-sm">
            {board.shopping.open === 0
              ? t('householdToday.shoppingEmpty')
              : t('householdToday.shoppingOpen', { count: board.shopping.open })}
          </p>
          {board.shopping.names.length > 0 ? (
            <p className="text-xs text-ink-muted">{board.shopping.names.join(' · ')}</p>
          ) : null}
          <button
            type="button"
            onClick={() => onOpenList(null)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('householdToday.toList')}
          </button>
        </section>
      ) : null}

      {board && !child && board.stars.length > 0 ? (
        <section aria-labelledby="household-today-stars" className="kw-tile flex flex-col gap-1">
          <h3 id="household-today-stars" className="kw-kicker">
            {t('householdToday.stars')}
          </h3>
          <p className="text-sm">
            {board.stars
              .map((entry) =>
                t('householdToday.starsOf', { name: entry.name, count: entry.balance }),
              )
              .join(' · ')}
          </p>
        </section>
      ) : null}

      {onOpenFamilyDisplay && householdId ? (
        <button
          type="button"
          onClick={onOpenFamilyDisplay}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('householdToday.familyDisplay')}
        </button>
      ) : null}
    </section>
  );
}
